import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';

import { combineLatest, from } from 'rxjs';
import { map, share, tap } from 'rxjs/operators';

import { BusyIndicatorService } from '../../../../gamma/busyindicator';
import { Mixin } from '../../../../gamma/core';
import { FormBaseComponent, IRegistrationFormConfigs } from '../../../../gamma/form/form-base.component';
import { FormMixin, IRegisteredForm } from '../../../../gamma/form/form.mixin';
import { IFormValidationMessage } from '../../../../gamma/form/formvalidationmessage.service';
import { HttpService } from '../../../../gamma/http';
import { UuidService } from '../../../../gamma/utils/uuid.service';

import { environment } from '../../../../environments/environment';
import { U2000_TranslatePartialLoader } from '../../../U2000_core';
import { U2000_AlertServiceMixin } from '../../../U2000_core/U2000_mixins/U2000_alert-service.mixin';
import { U2000_VerifyEmailNavigationStepsEnum } from './U2000_verifyemail';

@Injectable()
@Mixin([U2000_AlertServiceMixin, FormMixin])
export class U2000_VerifyEmailService implements U2000_AlertServiceMixin, FormMixin {
    // There to avoid using the constructor's name that is not available when minified.
    static className = 'U2000_VerifyEmailService';

    //#region Mixin requirements
    // Alert mixin requirements
    alertsAdded: IU2000_Alert[];
    alertAdded$: EventEmitter<IU2000_Alert>;
    alertUpdated$: EventEmitter<IU2000_Alert>;
    alertsCleared$: EventEmitter<any>;
    initAlert: () => void;
    addAlert: (alert: IU2000_Alert) => void;
    updateAlert: (alert: IU2000_Alert) => void;
    clearAlerts: () => void;
    // Form mixin requirements
    formValidationMessages: IFormValidationMessage[];
    registeredForms: IRegisteredForm[];
    registerForm: (form: FormBaseComponent, configs: IRegistrationFormConfigs) => void;
    unregisterForm: (form: FormBaseComponent) => void;
    hasDirtyForms: () => boolean;
    cleanupForms: () => void;
    //#endregion

    _currentNavigationComponentId: U2000_VerifyEmailNavigationStepsEnum;

    valid: EventEmitter<string>;
    error: EventEmitter<HttpErrorResponse>;
    componentConfigs: IU2000_VerifyEmailInitResultDto;
    validatecodeResult: IU2000_VerifyEmailCodeResultDto;
    sendCodeResult: IU2000_VerifyEmailSendCodeResultDto;

    emailToValidate: string;
    contactMediaControlFlowUuid: string;

    constructor(
        public http: HttpClient,
        private httpService: HttpService,
        private translatePartialLoader: U2000_TranslatePartialLoader,
        private busyIndicatorService: BusyIndicatorService,
        private uuidService: UuidService,
    ) {
        this.initAlert();

        this.formValidationMessages = [];
        this.navigateTo(U2000_VerifyEmailNavigationStepsEnum.verifyCode);
    }

    resolve(email: string) {
        this.emailToValidate = email;
        this.busyIndicatorService.show(null, 'Verify email');
        const promise = new Promise((resolve, reject) => {
            combineLatest([this.initComponent(), this.initTranslation()]).subscribe(
                result => {
                    this.componentConfigs = result[0];
                    this.sendCodeResult = this.componentConfigs.sendCodeResult;

                    this.busyIndicatorService.hide(null, 'Verify email');
                    resolve(result[0]);
                },
                error => {
                    reject();

                    this.busyIndicatorService.hide(null, 'Verify email');
                    this.valid.emit(error);
                },
            );
        });

        return from(promise);
    }

    navigateTo(componentId: U2000_VerifyEmailNavigationStepsEnum) {
        this._currentNavigationComponentId = componentId;
    }

    get currentNavigationComponentId() {
        return this._currentNavigationComponentId;
    }

    initComponent() {
        this.contactMediaControlFlowUuid = this.uuidService.generateUnsafeUuid();

        let url = environment.apiUrl + 'U2000/verifyEmail/init';
        url = url + '?contactMediaControlFlowUuid=' + this.contactMediaControlFlowUuid;
        url = url + '&email=' + encodeURIComponent(this.emailToValidate);

        return this.http.get<IU2000_VerifyIdentityInitDtoResponse>(url, this.httpService.optionsWithExtra({ silent: true, globalErrorHandling: false })).pipe(
            map(res => {
                return res.result;
            }),
            tap(res => {
                this.componentConfigs = res;
                this.sendCodeResult = res.sendCodeResult;
            }),
            share(),
        );
    }

    initTranslation() {
        return this.translatePartialLoader.getPartialTranslation('U2000_VerifyEmail').pipe(share());
    }

    verifyIdentityCode(formValue: IU2000_VerifyEmailCodeDtoRequest) {
        formValue.contactMediaControlId = this.sendCodeResult.contactMediaControlId;
        formValue.contactMediaControlFlowUuid = this.contactMediaControlFlowUuid;
        formValue.securityKey = this.sendCodeResult.securityKey;

        return this.http.post<IU2000_VerifyIdentityCodeDtoResponse>(environment.apiUrl + 'U2000/verifyIdentity/validatecode', formValue).pipe(
            map(res => {
                return res.result;
            }),
            tap(res => {
                this.validatecodeResult = res;
            }),
        );
    }

    sendVerificationCode(formValue: IU2000_VerifyEmailSendCodeDtoRequest) {
        formValue.contactMediaControlFlowUuid = this.contactMediaControlFlowUuid;
        return this.http.post<IU2000_VerifyEmailSendCodeDtoResponse>(environment.apiUrl + 'U2000/verifyEmail/sendcode', formValue).pipe(
            map(res => {
                return res.result;
            }),
            tap(res => {
                this.sendCodeResult = res;
            }),
        );
    }

    resendVerificationCode(formValue: IU2000_VerifyEmailResendCodeDtoRequest) {
        formValue.contactMediaControlId = this.sendCodeResult.contactMediaControlId;
        formValue.contactMediaControlFlowUuid = this.contactMediaControlFlowUuid;
        formValue.securityKey = this.sendCodeResult.securityKey;
        return this.http.post<IU2000_VerifyEmailResendCodeDtoResponse>(environment.apiUrl + 'U2000/verifyEmail/resendcode', formValue).pipe(
            map(res => {
                return res.result;
            }),
            tap(res => {}),
        );
    }

    onValid() {
        this.valid.emit(this.sendCodeResult.securityKey);
    }
}
